import { useMemo, useCallback, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useUpdateJustification, useDeleteJustification, useUpsertJustification } from './useSupplementMutations';
import type { DisplayRoom, DisplayLineItem } from '@/types/supplement';

// ============================================================================
// UI HELPERS
// ============================================================================

/**
 * Hook for managing collapse/expand state of a list of items
 */
export function useCollapseState<T>(items: T[], keyFn: (item: T, index: number) => string | number) {
    // Initialize with all items collapsed by computing all keys on first render
    const [collapsedIds, setCollapsedIds] = useState<Set<string | number>>(() => {
        return new Set(items.map((item, index) => keyFn(item, index)));
    });

    const itemKeys = useMemo(() => items.map((item, index) => keyFn(item, index)), [items, keyFn]);

    const toggleAll = useCallback((open?: boolean) => {
        if (open === false || collapsedIds.size < itemKeys.length) {
            setCollapsedIds(new Set(itemKeys));
        } else {
            setCollapsedIds(new Set());
        }
    }, [collapsedIds.size, itemKeys]);

    const toggleItem = useCallback((id: string | number, open?: boolean) => {
        setCollapsedIds((prev) => {
            const next = new Set(prev);
            if (open === true) {
                next.delete(id);
            } else if (open === false) {
                next.add(id);
            } else {
                // Toggle
                if (next.has(id)) {
                    next.delete(id);
                } else {
                    next.add(id);
                }
            }
            return next;
        });
    }, []);

    const isCollapsed = useCallback((id: string | number) => collapsedIds.has(id), [collapsedIds]);
    const allCollapsed = collapsedIds.size === itemKeys.length;

    return { toggleAll, toggleItem, isCollapsed, allCollapsed };
}

/**
 * Hook for search/filter logic across rooms and line items
 * Supports multiple search terms with OR logic (matches if ANY term is found)
 */
export function useRoomSearch(rooms: DisplayRoom[], searchTerms: string[]) {
    return useMemo(() => {
        if (searchTerms.length === 0) return rooms;

        const lowerTerms = searchTerms.map(term => term.toLowerCase());
        return rooms
            .map((room) => {
                const matchingLineItems = room.items.filter(
                    (item: DisplayLineItem) => {
                        const description = item.description.toLowerCase();
                        const category = item.category?.toLowerCase() || '';
                        return lowerTerms.some(term => 
                            description.includes(term) || category.includes(term)
                        );
                    }
                );

                const roomNameMatches = lowerTerms.some(term => 
                    room.roomName.toLowerCase().includes(term)
                );

                if (roomNameMatches) {
                    return room;
                }

                if (matchingLineItems.length > 0) {
                    return {
                        ...room,
                        items: matchingLineItems,
                    };
                }

                return null;
            })
            .filter(Boolean) as DisplayRoom[];
    }, [rooms, searchTerms]);
}

/**
 * Hook for memoized line item calculations
 */
export function useLineItemCalculations(item: DisplayLineItem) {
    return useMemo(() => {
        // Ensure we have arrays (guard against undefined/null)
        const insurerItems = Array.isArray(item.insurerItems) ? item.insurerItems : [];
        const contractorItems = Array.isArray(item.contractorItems) ? item.contractorItems : [];
        
        // Calculate totals inline
        const insurerTotal = insurerItems.reduce((sum, item) => sum + item.rcvCents, 0);
        const contractorTotal = contractorItems.reduce((sum, item) => sum + item.rcvCents, 0);
        const diffCents = contractorTotal - insurerTotal;

        const hasInsurer = insurerItems.length > 0;
        const hasContractor = contractorItems.length > 0;
        const isUnmatched = !hasInsurer || !hasContractor;
        const hasContractorOnly = hasContractor && !hasInsurer;

        return {
            insurerTotal,
            contractorTotal,
            diffCents,
            isUnmatched,
            hasInsurer,
            hasContractor,
            hasContractorOnly,
        };
    }, [item.insurerItems, item.contractorItems]);
}

/**
 * Hook for handling justification save logic with toasts and callbacks
 * Uses UPDATE for existing justifications
 */
export function useJustificationSave(supplementId: number, onSuccess?: () => void) {
    const { toast } = useToast();
    const updateJustificationMutation = useUpdateJustification();

    const saveJustification = useCallback(
        async (
            justificationId: number,
            data: {
                justificationText: string;
                codeBookTitle?: string;
                chapter?: string;
                snippet?: string;
            }
        ) => {
            if (!justificationId) {
                console.error('Cannot save: missing justificationId');
                return;
            }

            try {
                await updateJustificationMutation.mutateAsync({
                    supplementId,
                    justificationId,
                    data: {
                        justificationText: data.justificationText || null,
                        codeBookTitle: data.codeBookTitle || null,
                        chapter: data.chapter || null,
                        snippet: data.snippet || null,
                    },
                });

                toast({
                    title: 'Success',
                    description: 'Justification updated successfully',
                });

                onSuccess?.();
            } catch (error: any) {
                console.error('Failed to update justification:', error);
                toast({
                    title: 'Error',
                    description: error.response?.data?.message || 'Failed to update justification',
                    variant: 'destructive',
                });
            }
        },
        [supplementId, updateJustificationMutation, toast, onSuccess]
    );

    return {
        saveJustification,
        isSaving: updateJustificationMutation.isPending,
    };
}

/**
 * Hook for handling justification upsert logic (create or update) with toasts and callbacks
 * Uses UPSERT - creates if doesn't exist, updates if it does
 */
export function useJustificationUpsert(
    supplementId: number,
    lineItemMatchGroupId?: number | null,
    lineItemId?: number | null,
    onSuccess?: () => void
) {
    const { toast } = useToast();
    const upsertJustificationMutation = useUpsertJustification();

    const upsertJustification = useCallback(
        async (data: {
            justificationText: string;
            codeBookTitle?: string;
            chapter?: string;
            snippet?: string;
        }) => {
            try {
                await upsertJustificationMutation.mutateAsync({
                    supplementId,
                    lineItemMatchGroupId,
                    lineItemId,
                    data: {
                        justificationText: data.justificationText || null,
                        codeBookTitle: data.codeBookTitle || null,
                        chapter: data.chapter || null,
                        snippet: data.snippet || null,
                    },
                });

                toast({
                    title: 'Success',
                    description: 'Justification saved successfully',
                });

                onSuccess?.();
            } catch (error: any) {
                console.error('Failed to upsert justification:', error);
                toast({
                    title: 'Error',
                    description: error.message || 'Failed to save justification',
                    variant: 'destructive',
                });
            }
        },
        [supplementId, lineItemMatchGroupId, lineItemId, upsertJustificationMutation, toast, onSuccess]
    );

    return {
        upsertJustification,
        isSaving: upsertJustificationMutation.isPending,
    };
}

/**
 * Hook for handling justification delete logic with toasts and callbacks
 */
export function useJustificationDelete(supplementId: number, onSuccess?: () => void) {
    const { toast } = useToast();
    const deleteJustificationMutation = useDeleteJustification();

    const deleteJustification = useCallback(
        async (justificationId: number) => {
            if (!justificationId) {
                console.error('Cannot delete: missing justificationId');
                return;
            }

            try {
                await deleteJustificationMutation.mutateAsync({
                    supplementId,
                    justificationId,
                });

                toast({
                    title: 'Success',
                    description: 'Justification deleted successfully',
                });

                onSuccess?.();
            } catch (error: any) {
                console.error('Failed to delete justification:', error);
                toast({
                    title: 'Error',
                    description: error.response?.data?.message || 'Failed to delete justification',
                    variant: 'destructive',
                });
            }
        },
        [supplementId, deleteJustificationMutation, toast, onSuccess]
    );

    return {
        deleteJustification,
        isDeleting: deleteJustificationMutation.isPending,
    };
}

/**
 * Hook for calculating totals from rooms
 */
export function useTotalCalculations(
    rooms: DisplayRoom[],
    totalSupplementCents?: number,
    totalMoneyFoundCents?: number
) {
    const calculatedSupplementCents = useMemo(
        () => rooms.reduce((sum, r) => sum + r.supplementAmountCents, 0),
        [rooms]
    );
    const calculatedMoneyFoundCents = useMemo(
        () => rooms.reduce((sum, r) => sum + r.moneyFoundCents, 0),
        [rooms]
    );

    return {
        supplementTotal: totalSupplementCents || calculatedSupplementCents,
        moneyFoundTotal: totalMoneyFoundCents || calculatedMoneyFoundCents,
    };
}

/**
 * Hook for processing room card data (grouping, sorting, display logic)
 */
export function useRoomCardData(room: DisplayRoom) {
    return useMemo(() => {
        // Group items by category
        const unmatchedContractorItems = room.items.filter(i => i.category === 'unmatched_contractor');
        const underscopedItems = room.items.filter(i => i.category === 'underscoped');
        const overscopedItems = room.items.filter(i => i.category === 'overscoped');
        const unmatchedInsurerItems = room.items.filter(i => i.category === 'unmatched_insurer');
        const perfectMatchItems = room.items.filter(i => i.category === 'perfect_match');

        // Build display room name with both contractor and insurer if available
        // Support multiple room names (for multi-room match groups)
        // Insurer on left, Contractor on right (matching the column layout)
        const displayRoomName = (() => {
            const hasContractorRooms = room.contractorRoomNames && room.contractorRoomNames.length > 0;
            const hasInsurerRooms = room.insurerRoomNames && room.insurerRoomNames.length > 0;
            
            if (hasInsurerRooms && hasContractorRooms) {
                const insurerDisplay = room.insurerRoomNames!.sort().join(', ');
                const contractorDisplay = room.contractorRoomNames!.sort().join(', ');
                return `${insurerDisplay} ↔ ${contractorDisplay}`;
            }
            
            return room.roomName;
        })();

        return {
            unmatchedContractorItems,
            underscopedItems,
            overscopedItems,
            unmatchedInsurerItems,
            perfectMatchItems,
            displayRoomName,
            totalItems: room.items.length,
        };
    }, [room]);
}
